import React from "react";
import TimesheetWorkerInput from "../../../screens/user/TimesheetWorkerInput";
import { MockedProvider } from "@apollo/client/testing/react";
import {
    buildBreakdown,
    buildBreakdownFromTrackingDates,
    buildContract,
    buildMission,
    buildMissionInfo,
    buildMissionVacationStatus,
    buildTimesheet,
} from "../../factories";
import {
    GET_DURATION_BREAKDOWN,
    GET_MISSION_VACATION_STATUS,
    GET_TIME_SHEET,
    SAVE_TIME_SHEET,
    SUBMIT_EXACT_TIME_SHEET,
} from "../../../graphql/timesheet";
import { GET_MISSION } from "../../../graphql/core";
import type { Meta, StoryObj } from "@storybook/react";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import {
    DateScheduleInput,
    EarningType,
    Repetition,
    TimeSheetState,
    Unit,
} from "../../../__generated__/graphql";
import { formatDateForGraphql } from "../../../utils";
import { addDays } from "date-fns";
import { StoryStackNavigatorContainer } from "../../display-helpers";

const Stack = createNativeStackNavigator<{
    TimesheetWorkerInput: { id: string };
}>();

const StoryComponent = ({ id }: { id: string }) => {
    const mocks = [
        {
            request: {
                query: GET_TIME_SHEET,
                variables: { timeSheetId: "test-open" },
            },
            result: () => ({
                data: {
                    getTimeSheet: buildTimesheet({ status: TimeSheetState.Open }),
                },
            }),
        },
        {
            request: {
                query: GET_TIME_SHEET,
                variables: { timeSheetId: "test-open-hourly" },
            },
            result: () => ({
                data: {
                    getTimeSheet: buildTimesheet({
                        id: "test-open-hourly",
                        status: TimeSheetState.Open,
                        fromDate: formatDateForGraphql(addDays(new Date(), -6)),
                        toDate: formatDateForGraphql(addDays(new Date(), -1)),
                        acceptedAt: null,
                        autoAcceptAt: null,
                        submittedAt: null,
                        rejectedAt: null,
                        acceptedBy: null,
                        missionInfo: buildMissionInfo({
                            id: "mission-open-hourly",
                            earningType: EarningType.Hourly,
                        }),
                    }),
                },
            }),
        },
        {
            request: {
                query: GET_TIME_SHEET,
                variables: { timeSheetId: "test-submitted" },
            },
            result: () => ({
                data: {
                    getTimeSheet: buildTimesheet({ status: TimeSheetState.Submitted }),
                },
            }),
        },
        {
            request: {
                query: GET_TIME_SHEET,
                variables: { timeSheetId: "test-invalid" },
            },
            result: () => ({
                data: {
                    getTimeSheet: buildTimesheet({
                        status: TimeSheetState.Open,
                        fromDate: formatDateForGraphql(addDays(new Date(), -1)),
                        toDate: formatDateForGraphql(addDays(new Date(), 0)),
                        trackingDates: [],
                    }),
                },
            }),
        },
        {
            request: {
                query: GET_TIME_SHEET,
                variables: { timeSheetId: "test-45hours" },
            },
            result: () => ({
                data: {
                    getTimeSheet: buildTimesheet({
                        status: TimeSheetState.Open,
                        fromDate: formatDateForGraphql(addDays(new Date(), 0)),
                        toDate: formatDateForGraphql(addDays(new Date(), 2)),
                        missionInfo: buildMissionInfo({ id: "mission-45hours" }),
                        trackingDates: [],
                    }),
                },
            }),
        },
        {
            request: {
                query: GET_MISSION,
                variables: () => true,
            },
            result: ({ id }: { id: string }) => {
                if (id === "mission-45hours") {
                    return {
                        data: {
                            getMission: buildMission({
                                currentContract: buildContract({
                                    startDate: formatDateForGraphql(addDays(new Date(), -7)),
                                    endDate: null,
                                    hoursPerWeekFullTime: 45,
                                    workingTimeClassicSpec: {
                                        repetition: Repetition.Week,
                                        unit: Unit.Hours,
                                        unitCount: 45,
                                        dailyHours: null,
                                    },
                                }),
                            }),
                        },
                    };
                }
                return {
                    data: {
                        getMission: buildMission({
                            currentContract: buildContract({
                                startDate: formatDateForGraphql(addDays(new Date(), -7)),
                                endDate: null,
                            }),
                        }),
                    },
                };
            },
        },
        {
            request: {
                query: GET_DURATION_BREAKDOWN,
                variables: () => true,
            },
            maxUsageCount: Number.POSITIVE_INFINITY,
            result: ({ trackingDates }: { trackingDates: [DateScheduleInput] }) => {
                return {
                    data: {
                        getDurationBreakdown: buildBreakdown(
                            buildBreakdownFromTrackingDates(trackingDates),
                        ),
                    },
                };
            },
        },
        {
            request: {
                query: SAVE_TIME_SHEET,
                variables: () => true,
            },
            result: ({
                trackingDates,
            }: {
                timeSheetId: string;
                trackingDates: [DateScheduleInput];
            }) => {
                // eslint-disable-next-line no-console
                console.log("Save timesheet", JSON.stringify(trackingDates, null, 2));
                return {
                    data: {
                        saveTimeSheet: buildTimesheet({}),
                    },
                };
            },
        },
        {
            request: {
                query: SUBMIT_EXACT_TIME_SHEET,
                variables: () => true,
            },
            result: ({
                trackingDates,
            }: {
                timeSheetId: string;
                trackingDates: [DateScheduleInput];
            }) => {
                // eslint-disable-next-line no-console
                console.log("Submit timesheet", JSON.stringify(trackingDates, null, 2));
                return {
                    data: {
                        submitExactTimeSheet: { id: "timesheet-id" },
                    },
                };
            },
        },
        {
            request: {
                query: GET_MISSION_VACATION_STATUS,
                variables: () => true,
            },
            maxUsageCount: Number.POSITIVE_INFINITY,
            result: () => {
                return {
                    data: {
                        getMissionVacationStatus: buildMissionVacationStatus({
                            availableVacationMinutes: 180, // 3 hours
                            availablePendingVacationMinutes: 60, // 1 hour available not yet approved
                        }),
                    },
                };
            },
        },
    ];
    return (
        <MockedProvider mocks={mocks}>
            <StoryStackNavigatorContainer>
                <Stack.Navigator initialRouteName="TimesheetWorkerInput">
                    <Stack.Screen
                        name="TimesheetWorkerInput"
                        component={TimesheetWorkerInput}
                        initialParams={{ id }}
                    />
                </Stack.Navigator>
            </StoryStackNavigatorContainer>
        </MockedProvider>
    );
};
const meta = {
    title: "Timesheet/Worker input",
    component: StoryComponent,
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Cycle: Story = {
    args: {
        id: "test-open",
    },
};

export const OpenStatusMonthly: Story = {
    args: {
        id: "test-open",
    },
};

export const OpenStatusHourly: Story = {
    args: {
        id: "test-open-hourly",
    },
};

export const SubmittedStatus: Story = {
    args: {
        id: "test-submitted",
    },
};

export const InvalidTimesheet: Story = {
    args: {
        id: "test-invalid",
    },
};

export const Contract45Hours: Story = {
    args: {
        id: "test-45hours",
    },
};
